import React, { useMemo, useRef } from 'react';
import {
	DataGrid,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	gridClasses,
} from '@mui/x-data-grid';
import { Typography, Stack, Box, useTheme } from '@mui/material';
import { useGetDealPoliciesQuery } from '@/features/deals/dealsApi';
import LoadingDataGridRowOverlay from '@/components/table/DataGridComponents/LoadingDataGridRowOverlay/index.jsx';
import { useObjectContext } from '@/hooks/useObjectContext';
import { formatCurrency, formatDate, getUpperCaseHeader } from '@/utils/constants';
import { getPolicyTransactionInvoiceLabel } from '@/utils/policyTransactionLabel';
import { GetMenuLink } from '../PolicyTransactionTable/GetMenuLink.jsx';
import {
	NoRowsErrorOverlay,
	NoRowsOverlay,
} from '../../table/DataGridComponents/NoRowsOverlay.jsx';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext.jsx';
import DataGridColumnMenu from '../../table/DataGridComponents/DataGridColumnMenu/index.jsx';

const Toolbar = () => (
	<GridToolbarContainer>
		<GridToolbarColumnsButton />
	</GridToolbarContainer>
);

//use the no rows over component

export const columns = [
	{
		field: 'InvoiceNumber',
		headerName: 'Invoice number',
		sortable: false,
		flex: 2,
		minWidth: 200,
		renderCell: ({ row }) => {
			const invoiceNumber = getPolicyTransactionInvoiceLabel(row);
			return (
				<Stack spacing={1}>
					<Typography>{invoiceNumber}</Typography>
				</Stack>
			);
		},
	},
	{
		field: 'policyNoClassRisk',
		headerName: 'Policy No / Class of Risk',
		sortable: false,
		flex: 2,
		minWidth: 220,
		renderCell: ({ row }) => (
			<Stack spacing={0.5}>
				<Typography>{row?.PolicyNumber ?? '-'}</Typography>
				<Typography>{row?.ClassOfRiskName ?? '-'}</Typography>
			</Stack>
		),
	},
	{
		field: 'TransactionType',
		headerName: 'Type',
		sortable: false,
		flex: 2,
		minWidth: 200,
		renderCell: ({ row }) => (
			<Stack spacing={1}>
				<Typography>{row?.TransactionTypeName ?? '-'}</Typography>
			</Stack>
		),
	},
	{
		field: 'PolicyEffectiveDate',
		headerName: 'Effective date',
		sortable: true,
		flex: 1,
		minWidth: 120,
		renderCell: ({ row }) => <Typography>{formatDate(row?.EffectiveDate, false)}</Typography>,
	},
	{
		field: 'Balance',
		headerName: 'Balance',
		sortable: true,
		flex: 1,
		minWidth: 120,
		renderCell: ({ row }) =>
			formatCurrency({
				num: row.Balance ?? 0,
				showDecimal: true,
			}),
	},
];

export const DebtorsInvoiceDataGrid = ({
	showActionsColumn = false,
	disableColumnWidth = false,
	disableColumnMenu = false,
}) => (
	<ColumnControlContextProvider variant='debtorInvoices'>
		<DebtorsInvoiceDataGridContent
			{...{ showActionsColumn, disableColumnWidth, disableColumnMenu }}
		/>
	</ColumnControlContextProvider>
);

const DebtorsInvoiceDataGridContent = ({
	showActionsColumn,
	disableColumnWidth,
	disableColumnMenu,
}) => {
	const theme = useTheme();

	const elementRef = useRef(null);

	const { dealId, dealRow } = useObjectContext();
	const policiesQuery = useGetDealPoliciesQuery({ dealId }, { skip: !dealId });

	const rows = useMemo(() => {
		return (policiesQuery?.data?.transactions ?? []).map((r) => ({
			...r,
			uniqueId: `trans-${r.Id}`,
		}));
	}, [policiesQuery?.data?.transactions]);

	const error = policiesQuery.isError;
	const loading = !error && !policiesQuery.isSuccess;

	const isFirstOfTransactions = (row) =>
		!rows.find((r) => r.uniqueId === row.uniqueId && rows.indexOf(r) < rows.indexOf(row));

	const gridColumns = useMemo(() => {
		if (!showActionsColumn) return columns;
		return [
			...columns,
			{
				field: 'actions',
				headerName: '',
				width: 40,
				resizable: false,
				hideable: false,
				sortable: false,
				renderCell: ({ row }) => (
					<Box
						className='actions-cell'
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							width: '100%',
						}}
					>
						<GetMenuLink
							deal={dealRow}
							hideViewDeal
							policy={row}
							slotProps={{
								button: {
									size: 'small',
								},
							}}
						/>
					</Box>
				),
			},
		];
	}, [showActionsColumn, dealRow]);

	const { columns: columnsWithWidths, ...gridColumnProps } = useDataGridColumnProps(gridColumns, {
		disableColumnWidth,
		disableColumnVisibility: disableColumnMenu,
	});

	const rowHeight = useMemo(() => (rows.length > 0 ? '100%' : '20vh'), [rows.length]);

	return (
		<Box
			className='MuiDataGrid-parentDiv'
			sx={{
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				minHeight: '20vh',
				maxHeight: '50vh',
				height: rowHeight,
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: '8px',
				backgroundColor: theme.palette.background.paper,
				'& .MuiDataGrid-root': {
					// DataGrid sets height: 'auto' on itself; override so it fills the flex
					// parent and uses its internal scroll instead of growing forever.
					height: '100% !important',
				},
			}}
		>
			<DataGrid
				ref={elementRef}
				key={rows.length > 0 ? rows[0].uniqueId : 'empty'}
				rows={rows}
				getRowHeight={() => 'auto'}
				getRowId={(row) => row.uniqueId}
				getRowClassName={(params) => {
					const { row } = params;
					const firstTrans = isFirstOfTransactions(row);
					const isFirst = rows.indexOf(row) === 0;
					return `row-transaction-id-${row.uniqueId}${
						firstTrans && !isFirst ? ' first-row-of-transaction' : ''
					}`;
				}}
				columns={columnsWithWidths}
				disableRowSelectionOnClick
				disableSelectionOnClick
				disableColumnFilter
				disableColumnSelector={disableColumnMenu}
				disableColumnMenu={disableColumnMenu}
				{...gridColumnProps}
				hideFooterPagination
				hideFooter
				sx={{
					'& .MuiDataGrid-editInputCell': {
						backgroundColor: 'transparent',
					},
					'--DataGrid-overlayHeight': '3em',
					'& .MuiDataGrid-cell': {
						display: 'flex',
						alignItems: 'center',
						padding: '8px 10px',
						borderTop: 'none !important',
					},
					'& .MuiDataGrid-row:hover': {
						backgroundColor: 'transparent',
					},
					'& .first-row-of-transaction': {
						borderTop: '1px solid var(--DataGrid-rowBorderColor) !important',
					},
					border: 'none',
					[`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
						outline: 'none',
					},
					[`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
						{
							outline: 'none',
						},
					[`& .${gridClasses.columnHeader}`]: {
						borderRight: 'none !important',
					},
					'& .MuiDataGrid-cell[data-field="actions"]': {
						position: 'sticky',
						right: 0,
						backgroundColor: theme.palette.background.paper,
						zIndex: 2,
						padding: 0,
						boxShadow: '-2px 0 4px -2px rgba(0,0,0,0.1)',
						borderRight: 'none !important',
						borderLeft: 'none !important',
					},
					'& .MuiDataGrid-columnHeader[data-field="actions"]': {
						resize: 'none',
						padding: 0,
						borderRight: 'none !important',
						borderLeft: 'none !important',
						position: 'sticky',
						right: 0,
						backgroundColor: 'var(--DataGrid-containerBackground)',
						zIndex: 3,
					},
					'& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnSeparator':
						{
							display: 'none',
							visibility: 'hidden',
						},
					'& .MuiDataGrid-virtualScroller': {
						overflowX: 'auto !important',
					},
					borderRadius: '8px',
				}}
				loading={loading}
				slots={{
					noRowsOverlay: (props) =>
						error ? (
							<NoRowsErrorOverlay {...props} objectType='invoices' />
						) : (
							<NoRowsOverlay {...props} objectType='invoices' />
						),
					...(!disableColumnMenu && { toolbar: Toolbar, columnMenu: DataGridColumnMenu }),
				}}
				slotProps={{
					loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' },
				}}
			/>
			<LoadingDataGridRowOverlay />
		</Box>
	);
};
